import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ago } from "@/components/shell/view";
import { useForecastHistory } from "@/hooks/world";
import { useStore } from "@/model/store";

/**
 * `409 STALE_FORECAST` (API plan §7.2): someone else saved this forecast after it loaded here, often Claude
 * over MCP. The app has already reloaded their version. "Use mine" saves this edit again on top of it.
 */
export function StaleForecastDialog() {
	const stale = useStore((s) => s.staleForecast);
	const dismiss = useStore((s) => s.dismissStaleForecast);
	const node = useStore((s) => (stale ? s.world.nodes[stale.node] : undefined));
	return (
		<Dialog open={!!stale} onOpenChange={(open) => !open && dismiss()}>
			<DialogContent>
				{stale && (
					<>
						<DialogHeader>
							<DialogTitle>This forecast changed while you were editing</DialogTitle>
							<DialogDescription>
								{node ? <strong className="font-medium text-foreground">{node.title}</strong> : "The forecast"} was saved by someone else after it loaded here.
								The chart now shows their version.
							</DialogDescription>
						</DialogHeader>
						{stale.node && <LatestVersion handle={stale.node} />}
						<DialogFooter>
							<Button variant="outline" onClick={dismiss}>
								Keep theirs
							</Button>
							<Button
								onClick={() => {
									const retry = stale.retry;
									dismiss();
									void retry();
								}}
							>
								Use mine
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

function LatestVersion({ handle }: { handle: string }) {
	const history = useForecastHistory(handle);
	const v = history.data?.versions[0];
	if (!v) return null;
	const who = v.origin === "mcp" ? `${v.client_name || "Claude"} (AI)` : (v.actor.name ?? "Someone");
	return (
		<p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
			<span className="font-medium">{who}</span>, {ago(v.created_at)}: {v.summary}
		</p>
	);
}
